/** Fields persisted on event translation save (Event Translations form). */
export const EVENT_TRANSLATION_SAVE_FIELDS = [
  'title',
  'titleShort',
  'descriptionShort',
  'descriptionLong',
  'overview',
  'attendInPerson',
  'watchOnline',
  'facebookText',
  'twitterText',
  'onDemandRibbonText',
  'venueLocation',
]

/**
 * Build PUT body from form state using an explicit field whitelist (never promo fields).
 * @param {Record<string, unknown>} state
 * @param {string[]} [fields]
 * @returns {Record<string, unknown>}
 */
export function buildEventTranslationSaveBody (state, fields = EVENT_TRANSLATION_SAVE_FIELDS) {
  const result = {}
  fields.forEach((key) => {
    if (state[key] !== undefined) {
      result[key] = state[key]
    }
  })
  return result
}

/** Copy fields on a spotlight translation row (desktop + mobile). */
const SPOTLIGHT_COPY_FIELDS = [
  'title',
  'subTitle',
  'description',
  'mobileHeader',
  'mobileSubHeader',
  'mobileDescription',
  'mobileButtonText',
]

/**
 * @param {import('immutable').Map<string, unknown> | null | undefined} spotlight
 * @returns {boolean}
 */
export function hasSpotlightTranslationCopy (spotlight) {
  if (spotlight == null || typeof spotlight.get !== 'function') {
    return false
  }
  return SPOTLIGHT_COPY_FIELDS.some((field) => {
    const value = spotlight.get(field)
    return value != null && String(value).trim() !== ''
  })
}

/**
 * @param {import('immutable').Map<string, unknown> | null | undefined} laSpotlightData
 * @param {import('immutable').Map<string, unknown> | null | undefined} spotlightByLanguage
 * @param {string} translationLanguage
 * @returns {string | null}
 */
export function resolveSpotlightId (laSpotlightData, spotlightByLanguage, translationLanguage) {
  const fromCurrent = laSpotlightData?.get?.('id')
  if (fromCurrent != null && fromCurrent !== '') {
    return String(fromCurrent)
  }
  const fromLang = spotlightByLanguage?.getIn?.([translationLanguage, 'id'])
  if (fromLang != null && fromLang !== '') {
    return String(fromLang)
  }
  const fromEn = spotlightByLanguage?.getIn?.(['en', 'id'])
  if (fromEn != null && fromEn !== '') {
    return String(fromEn)
  }
  return null
}

/**
 * @param {unknown} error
 * @returns {string}
 */
export function formatLiveAccessApiError (error) {
  if (error == null) {
    return 'Request failed'
  }
  if (typeof error === 'string') {
    return error
  }
  const message =
    error.response?.body?.errors?.[0]?.message ?? error.response?.data?.errors?.[0]?.message
  if (message) {
    return message
  }
  if (error.message) {
    return error.message
  }
  return 'Request failed'
}

/**
 * @param {{ label: string, value: string }[]} languages
 * @param {string} code
 * @returns {string}
 */
export function languageLabelForCode (languages, code) {
  const match = languages.find((lang) => lang.value === code)
  return match?.label ?? code
}

/**
 * Default spotlight copy when auto-creating a primary-language row (e.g. EN, ES).
 * @param {string} [language]
 * @returns {{ title: string, subTitle: string, description: string }}
 */
export function buildSpotlightPlaceholderCopy (language) {
  const code = (language || 'en').toUpperCase()
  return {
    title: `${code} - Spotlight title`,
    subTitle: `${code} - Spotlight subTitle`,
    description: `${code} - Spotlight description`,
  }
}

/**
 * @param {import('immutable').Map<string, unknown> | null | undefined} eventData
 * @returns {string | null}
 */
function readPrimaryLanguageFromEventData (eventData) {
  if (eventData == null || typeof eventData.get !== 'function') {
    return null
  }
  const primaryLanguage = eventData.get('primaryLanguage') || eventData.get('primary_language')
  return primaryLanguage != null && primaryLanguage !== '' ? String(primaryLanguage) : null
}

/**
 * @param {import('immutable').Map<string, unknown> | null | undefined} eventData
 * @returns {string}
 */
export function getEventPrimaryLanguage (eventData) {
  return readPrimaryLanguageFromEventData(eventData) || 'en'
}

/**
 * Primary language for edit-page flows (spotlight get-or-create, translation defaults).
 * @param {import('immutable').Map<string, unknown>} liveEventManagement
 * @param {string | number} eventId
 * @param {import('immutable').Map<string, unknown> | null | undefined} eventData
 * @returns {string}
 */
export function resolveEventPrimaryLanguageForEdit (liveEventManagement, eventId, eventData) {
  const fromEvent = readPrimaryLanguageFromEventData(eventData)
  if (fromEvent) {
    return fromEvent
  }

  const createEventData = liveEventManagement?.getIn?.(['createEvent', 'eventData'])
  if (createEventData && String(createEventData.get('id')) === String(eventId)) {
    const fromCreate = readPrimaryLanguageFromEventData(createEventData)
    if (fromCreate) {
      return fromCreate
    }
  }

  return 'en'
}
